import { useState, useEffect } from "react";
import { Home, BookOpen, FileText, Clock, ChevronRight, Check, X, RotateCcw, Trophy, BarChart3, Target, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import PricingSection from "@/components/PricingSection";
import { useTranslation, useLanguage } from "@/components/LanguageContext";

const SUPPORTED_LANGS = ["pt", "es"];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_wp-landing-builder/artifacts/p3s982cv_1.jpg";

const modules = [
  { id: 1, name: "Module 1 — Mathematics", questions: 12, available: true },
  { id: 2, name: "Module 2 — Physics", questions: 0, available: false },
  { id: 3, name: "Module 3 — Electrical Fundamentals", questions: 0, available: false },
  { id: 4, name: "Module 4 — Electronic Fundamentals", questions: 0, available: false },
  { id: 5, name: "Module 5 — Digital Techniques / Instruments", questions: 0, available: false },
  { id: 6, name: "Module 6 — Materials and Hardware", questions: 0, available: false },
  { id: 7, name: "Module 7 — Maintenance Practices", questions: 0, available: false },
  { id: 8, name: "Module 8 — Basic Aerodynamics", questions: 0, available: false },
  { id: 9, name: "Module 9 — Human Factors", questions: 0, available: false },
  { id: 10, name: "Module 10 — Aviation Legislation", questions: 0, available: false },
];

const module1Questions = [
  { q: "What is the value of 2³ × 3²?", opts: ["72", "36", "54", "48"], correct: 0 },
  { q: "Convert 0.75 to a fraction:", opts: ["3/4", "7/5", "4/3", "5/7"], correct: 0 },
  { q: "What is the square root of 144?", opts: ["14", "12", "11", "13"], correct: 1 },
  { q: "If x + 5 = 12, what is the value of x?", opts: ["5", "6", "7", "8"], correct: 2 },
  { q: "What is 15% of 200?", opts: ["25", "35", "30", "20"], correct: 2 },
  { q: "Convert 5/8 to decimal:", opts: ["0.625", "0.575", "0.685", "0.725"], correct: 0 },
  { q: "What is the result of log₁₀(1000)?", opts: ["2", "3", "4", "10"], correct: 1 },
  { q: "Simplify: 4² + 3² =", opts: ["25", "14", "49", "7"], correct: 0 },
];

function PhoneFrame({ children }) {
  return (
    <div className="phone-frame relative shrink-0 self-center">
      {/* Phone body */}
      <div className="absolute inset-0 bg-[#1a1a1a] rounded-[40px] shadow-2xl shadow-black/30 border border-slate-700/50" />
      {/* Screen */}
      <div className="absolute top-[12px] left-[12px] right-[12px] bottom-[12px] bg-[#0F172A] rounded-[30px] overflow-hidden">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-[#1a1a1a] rounded-b-2xl z-20" />
        {/* Screen content */}
        <div className="h-full overflow-hidden flex flex-col">
          {children}
        </div>
      </div>
    </div>
  );
}

function AppHome() {
  return (
    <div className="flex-1 overflow-y-auto no-scrollbar px-4 pt-9 pb-4 flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl overflow-hidden bg-[#0A1628] ring-1 ring-[#00A8FF]/20 shrink-0">
          <img src={LOGO_URL} alt="L4C" className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="text-white font-bold text-sm">L4C Part 66</p>
          <p className="text-slate-400 text-xs">Welcome back!</p>
        </div>
      </div>

      <div className="bg-gradient-to-r from-[#00A8FF]/20 to-[#F59E0B]/20 rounded-xl p-3.5 mb-3.5 border border-[#00A8FF]/20">
        <p className="text-[#00A8FF] text-[10px] font-bold uppercase tracking-wider mb-1.5">Overall Progress</p>
        <div className="flex items-end gap-3">
          <span className="text-white text-2xl font-black font-['Outfit']">8%</span>
          <span className="text-slate-400 text-[10px] mb-1">1/17 modules started</span>
        </div>
        <div className="mt-2.5 h-1.5 bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#00A8FF] to-[#F59E0B] rounded-full" style={{ width: "8%" }} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5 mb-3.5">
        <div className="bg-slate-800/60 rounded-xl p-3 border border-slate-700/50">
          <Target className="w-4 h-4 text-[#00A8FF] mb-1.5" />
          <p className="text-white font-bold text-base">12</p>
          <p className="text-slate-400 text-[10px] leading-tight">Available questions</p>
        </div>
        <div className="bg-slate-800/60 rounded-xl p-3 border border-slate-700/50">
          <Trophy className="w-4 h-4 text-[#F59E0B] mb-1.5" />
          <p className="text-white font-bold text-base">0</p>
          <p className="text-slate-400 text-[10px] leading-tight">Exams completed</p>
        </div>
      </div>

      <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider mb-2">Continue Studying</p>
      <div className="bg-slate-800/60 rounded-xl p-3 border border-slate-700/50 flex items-center justify-between mb-3.5">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-[#00A8FF]/10 flex items-center justify-center shrink-0">
            <span className="text-[#00A8FF] font-bold text-xs">M1</span>
          </div>
          <div className="min-w-0">
            <p className="text-white text-xs font-bold truncate">Module 1 — Mathematics</p>
            <p className="text-slate-400 text-[10px]">12 questions · Free</p>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />
      </div>

      {/* Today's tip card to fill space naturally */}
      <div className="bg-gradient-to-br from-slate-800/40 to-slate-800/20 rounded-xl p-3 border border-slate-700/40 mt-auto">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[#F59E0B] text-[10px] font-bold uppercase tracking-wider">Tip of the day</span>
        </div>
        <p className="text-slate-300 text-[11px] leading-snug">Practice 10 questions a day to maintain steady progress.</p>
      </div>
    </div>
  );
}

function AppModules({ onSelectModule }) {
  return (
    <div className="flex-1 overflow-y-auto no-scrollbar px-4 pt-10 pb-4">
      <p className="text-white font-bold text-lg font-['Outfit'] mb-1">EASA Part 66 Modules</p>
      <p className="text-slate-400 text-xs mb-5">Select a module to study</p>

      <div className="space-y-2.5">
        {modules.map((m) => (
          <button
            key={m.id}
            onClick={() => m.available && onSelectModule(m)}
            className={`w-full text-left rounded-xl p-4 flex items-center justify-between transition-all ${
              m.available
                ? "bg-slate-800/60 border border-slate-700/50 hover:border-[#00A8FF]/40"
                : "bg-slate-800/30 border border-slate-800/30 opacity-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold ${
                m.available ? "bg-[#00A8FF]/10 text-[#00A8FF]" : "bg-slate-700/50 text-slate-500"
              }`}>
                M{m.id}
              </div>
              <div>
                <p className={`text-sm font-bold ${m.available ? "text-white" : "text-slate-500"}`}>{m.name}</p>
                <p className="text-xs text-slate-500">
                  {m.available ? `${m.questions} questions · Free` : "Available in Full Pack"}
                </p>
              </div>
            </div>
            {m.available ? (
              <ChevronRight className="w-4 h-4 text-slate-500" />
            ) : (
              <span className="text-[9px] font-bold text-slate-600 bg-slate-700/50 px-2 py-1 rounded-full uppercase">Pro</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function AppExam({ onStartExam }) {
  return (
    <div className="flex-1 overflow-y-auto no-scrollbar px-4 pt-10 pb-4">
      <p className="text-white font-bold text-lg font-['Outfit'] mb-1">Exam Mode</p>
      <p className="text-slate-400 text-xs mb-5">Simulate a real EASA Part 66 exam</p>

      <div className="bg-slate-800/60 rounded-xl p-5 border border-slate-700/50 mb-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
            <FileText className="w-5 h-5 text-[#F59E0B]" />
          </div>
          <div>
            <p className="text-white font-bold text-sm">Module 1 Exam</p>
            <p className="text-slate-400 text-xs">8 questions · ~5 min</p>
          </div>
        </div>
        <p className="text-slate-400 text-xs mb-4 leading-relaxed">
          Test your Mathematics knowledge under conditions similar to the official exam.
        </p>
        <button
          onClick={onStartExam}
          className="w-full py-2.5 bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white text-sm font-bold rounded-lg"
        >
          Start Exam
        </button>
      </div>

      <div className="bg-slate-800/30 rounded-xl p-5 border border-slate-800/30 opacity-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center">
            <FileText className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <p className="text-slate-500 font-bold text-sm">Modules 2-17 Exam</p>
            <p className="text-slate-600 text-xs">Available in Full Pack</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AppHistory({ history }) {
  return (
    <div className="flex-1 overflow-y-auto no-scrollbar px-4 pt-10 pb-4">
      <p className="text-white font-bold text-lg font-['Outfit'] mb-1">History</p>
      <p className="text-slate-400 text-xs mb-5">Your exam results</p>

      {history.length === 0 ? (
        <div className="text-center py-12">
          <Clock className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">You haven't taken any exam yet</p>
          <p className="text-slate-600 text-xs mt-1">Go to the Exams tab to start</p>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((h, i) => (
            <div key={i} className="bg-slate-800/60 rounded-xl p-4 border border-slate-700/50">
              <div className="flex items-center justify-between mb-2">
                <p className="text-white text-sm font-bold">Module 1 — Mathematics</p>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  h.pct >= 75 ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                }`}>
                  {h.pct}%
                </span>
              </div>
              <p className="text-slate-400 text-xs">{h.correct}/{h.total} correct · {h.date}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuizScreen({ onFinish }) {
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);

  const question = module1Questions[current];

  const handleSelect = (idx) => {
    if (answered) return;
    setSelected(idx);
    setAnswered(true);
    if (idx === question.correct) setScore((s) => s + 1);
  };

  const handleNext = () => {
    if (current < module1Questions.length - 1) {
      setCurrent((c) => c + 1);
      setSelected(null);
      setAnswered(false);
    } else {
      setFinished(true);
    }
  };

  if (finished) {
    const pct = Math.round((score / module1Questions.length) * 100);
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-10">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
          pct >= 75 ? "bg-green-500/20" : "bg-red-500/20"
        }`}>
          {pct >= 75 ? <Trophy className="w-10 h-10 text-green-400" /> : <RotateCcw className="w-10 h-10 text-red-400" />}
        </div>
        <p className="text-white font-bold text-2xl font-['Outfit'] mb-1">{pct}%</p>
        <p className="text-slate-400 text-sm mb-1">{score}/{module1Questions.length} correct answers</p>
        <p className={`text-xs font-bold mb-6 ${pct >= 75 ? "text-green-400" : "text-red-400"}`}>
          {pct >= 75 ? "Passed! Excellent work." : "Not passed. Try again!"}
        </p>
        <button
          onClick={() => onFinish({ correct: score, total: module1Questions.length, pct, date: "Now" })}
          className="w-full py-3 bg-gradient-to-r from-[#00A8FF] to-[#3BC4FF] text-white text-sm font-bold rounded-lg"
        >
          View Results
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col px-4 pt-10 pb-4">
      <div className="flex items-center justify-between mb-4">
        <p className="text-slate-400 text-xs font-bold">MODULE 1 — MATHEMATICS</p>
        <p className="text-[#00A8FF] text-xs font-bold">{current + 1}/{module1Questions.length}</p>
      </div>
      <div className="h-1.5 bg-slate-700 rounded-full mb-6 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#00A8FF] to-[#F59E0B] rounded-full transition-all duration-300"
          style={{ width: `${((current + 1) / module1Questions.length) * 100}%` }}
        />
      </div>

      <p className="text-white text-base font-bold mb-6 leading-relaxed">{question.q}</p>

      <div className="space-y-2.5 flex-1">
        {question.opts.map((opt, idx) => {
          let cls = "bg-slate-800/60 border-slate-700/50 text-white";
          if (answered) {
            if (idx === question.correct) cls = "bg-green-500/20 border-green-500/40 text-green-300";
            else if (idx === selected) cls = "bg-red-500/20 border-red-500/40 text-red-300";
          } else if (idx === selected) {
            cls = "bg-[#00A8FF]/20 border-[#00A8FF]/40 text-[#00A8FF]";
          }
          return (
            <button
              key={idx}
              onClick={() => handleSelect(idx)}
              className={`w-full text-left p-3.5 rounded-xl border text-sm font-medium transition-all flex items-center justify-between ${cls}`}
            >
              <span>{opt}</span>
              {answered && idx === question.correct && <Check className="w-4 h-4 text-green-400" />}
              {answered && idx === selected && idx !== question.correct && <X className="w-4 h-4 text-red-400" />}
            </button>
          );
        })}
      </div>

      {answered && (
        <button
          onClick={handleNext}
          className="mt-4 w-full py-3 bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white text-sm font-bold rounded-lg"
        >
          {current < module1Questions.length - 1 ? "Next Question →" : "View Result"}
        </button>
      )}
    </div>
  );
}

function PhoneEmulator() {
  const [tab, setTab] = useState("home");
  const [screen, setScreen] = useState("tabs");
  const [history, setHistory] = useState([]);

  const tabs = [
    { id: "home", label: "Home", icon: Home },
    { id: "modules", label: "Modules", icon: BookOpen },
    { id: "exams", label: "Exams", icon: FileText },
    { id: "history", label: "History", icon: Clock },
  ];

  const handleFinishExam = (result) => {
    setHistory((h) => [result, ...h]);
    setScreen("tabs");
    setTab("history");
  };

  return (
    <PhoneFrame>
      {screen === "quiz" ? (
        <QuizScreen onFinish={handleFinishExam} />
      ) : (
        <>
          {tab === "home" && <AppHome />}
          {tab === "modules" && <AppModules onSelectModule={() => { setScreen("quiz"); }} />}
          {tab === "exams" && <AppExam onStartExam={() => { setScreen("quiz"); }} />}
          {tab === "history" && <AppHistory history={history} />}
        </>
      )}

      {/* Bottom Tab Bar */}
      {screen !== "quiz" && (
        <div className="bg-[#0A1628] border-t border-slate-700/50 flex items-center justify-around py-2 pb-4 shrink-0">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex flex-col items-center gap-0.5 px-3 py-1 transition-colors ${
                tab === t.id ? "text-[#00A8FF]" : "text-slate-500"
              }`}
            >
              <t.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{t.label}</span>
            </button>
          ))}
        </div>
      )}
    </PhoneFrame>
  );
}

export default function AppShowcasePage() {
  const [activeSection, setActiveSection] = useState("app-demo");
  const { lang: ctxLang } = useLanguage();
  const effectiveLang = SUPPORTED_LANGS.includes(ctxLang) ? ctxLang : "en";
  const { t } = useTranslation(effectiveLang);

  useEffect(() => {
    const sections = ["app-demo", "app-precos", "app-features"];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );
    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const subNavItems = [
    { id: "app-demo", label: t("appPage.subExperiment") },
    { id: "app-precos", label: t("appPage.subPricing"), cta: true },
    { id: "app-features", label: t("appPage.subFeatures") },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />

      {/* Sub-nav for App page */}
      <div className="fixed top-[112px] left-0 right-0 z-40 bg-[#0A1628] border-b border-[#1A2D4A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-8 h-14">
          {subNavItems.map((item) => {
            const isActive = activeSection === item.id;
            const shouldPulse = item.cta && !isActive;
            return (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={`relative text-[13px] font-semibold uppercase tracking-wider transition-all duration-300 py-3 ${
                  isActive
                    ? "text-white"
                    : item.cta
                    ? "text-[#F59E0B] animate-pulse-glow"
                    : "text-slate-500 hover:text-slate-300"
                }`}
              >
                {item.label}
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#00A8FF] to-[#F59E0B] rounded-full" />
                )}
                {shouldPulse && (
                  <span className="absolute inset-0 animate-pulse-glow" />
                )}
              </a>
            );
          })}
        </div>
      </div>

      {/* Hero + Emulator */}
      <div id="app-demo" className="bg-[#0A1628] pb-12 md:pb-16" style={{ paddingTop: "200px", scrollMarginTop: "168px" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-4 py-2 mb-6">
                <Smartphone className="w-3.5 h-3.5 text-[#00A8FF]" />
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-white/80">
                  {t("appPage.badge")}
                </span>
              </div>
              <h1 className="font-['Outfit'] text-4xl md:text-5xl font-black text-white leading-tight mb-5">
                {t("appPage.title1")}{" "}
                <span className="bg-gradient-to-r from-[#00A8FF] to-[#F59E0B] bg-clip-text text-transparent">
                  {t("appPage.titleHighlight")}
                </span>
              </h1>
              <p className="text-base text-slate-400 mb-8 max-w-lg leading-relaxed">
                {t("appPage.desc")}
              </p>

              <div className="space-y-4 mb-8">
                {[t("appPage.feat1"), t("appPage.feat2"), t("appPage.feat3"), t("appPage.feat4")].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#00A8FF]/20 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-[#00A8FF]" />
                    </div>
                    <p className="text-sm text-slate-300">{item}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <a href="#app-precos" className="bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white font-bold py-3 px-6 rounded-md text-sm shadow-lg shadow-amber-500/20">
                  {t("appPage.ctaPrimary")}
                </a>
                <Link to="/" className="bg-white/10 text-white font-bold py-3 px-6 rounded-md text-sm border border-white/20 hover:bg-white/20 transition-colors">
                  {t("appPage.ctaBack")}
                </Link>
              </div>
            </div>

            {/* Phone Emulator */}
            <div className="w-full flex justify-center items-center" data-testid="phone-emulator">
              <PhoneEmulator />
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <PricingSection lockedLang={effectiveLang} />

      {/* Features below */}
      <div id="app-features" className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A8FF] mb-2">{t("appPage.featLabel")}</p>
          <div className="w-12 h-0.5 bg-gradient-to-r from-[#00A8FF] to-[#F59E0B] mx-auto mb-6" />
          <h2 className="font-['Outfit'] text-3xl font-extrabold text-[#0A1628] mb-4">
            {t("appPage.featTitle")}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {[
            { icon: BookOpen, title: t("appPage.f1"), desc: t("appPage.f1d"), color: "#00A8FF" },
            { icon: Target, title: t("appPage.f2"), desc: t("appPage.f2d"), color: "#F59E0B" },
            { icon: BarChart3, title: t("appPage.f3"), desc: t("appPage.f3d"), color: "#22C55E" },
            { icon: Trophy, title: t("appPage.f4"), desc: t("appPage.f4d"), color: "#8B5CF6" },
          ].map((f, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:border-[#00A8FF]/30 transition-all">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: `${f.color}15` }}>
                <f.icon className="w-5 h-5" style={{ color: f.color }} />
              </div>
              <h3 className="font-['Outfit'] font-bold text-[#0A1628] mb-1">{f.title}</h3>
              <p className="text-sm text-slate-500">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Store buttons */}
      <div className="bg-[#0A1628] py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-white font-['Outfit'] font-bold text-xl mb-2">{t("appPage.storeSoon")}</p>
          <p className="text-slate-400 text-sm mb-8">{t("appPage.storeDesc")}</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <div className="flex items-center gap-3 bg-black text-white py-3 px-6 rounded-xl">
              <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              <div className="text-left"><div className="text-[9px] uppercase tracking-wider opacity-70">{t("appPage.comingSoonTo")}</div><div className="text-sm font-bold -mt-0.5">App Store</div></div>
            </div>
            <div className="flex items-center gap-3 bg-black text-white py-3 px-6 rounded-xl">
              <svg viewBox="0 0 512 512" className="w-7 h-7"><path d="M48 59.49v393a17 17 0 0 0 27.94 13.07L303 256 75.94 46.42A17 17 0 0 0 48 59.49z" fill="#2196F3"/><path d="M463.7 240.64l-96.37-55.58L325.08 256l42.25 70.94 96.37-55.58a17.13 17.13 0 0 0 0-30.72z" fill="#FFC107"/><path d="M75.94 46.42L303 256l42.25-70.94L136.47 29.81a17 17 0 0 0-60.53 16.61z" fill="#4CAF50"/><path d="M303 256L75.94 465.58a17 17 0 0 0 60.53 16.61l208.78-155.25L303 256z" fill="#F44336"/></svg>
              <div className="text-left"><div className="text-[9px] uppercase tracking-wider opacity-70">{t("appPage.comingSoonTo")}</div><div className="text-sm font-bold -mt-0.5">Google Play</div></div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer mini */}
      <div className="text-center text-sm text-slate-400 py-8 border-t border-slate-200">
        <p>&copy; 2026 L4C Aviation Academy. {t("appPage.rightsReserved")}</p>
        <Link to="/" className="text-[#00A8FF] hover:underline mt-2 inline-block">{t("appPage.backHome")}</Link>
      </div>
    </div>
  );
}
